import {
  type Props as JournalBankAccountDropdownProps,
  JournalBankAccountsDropdown,
} from "@/components/shared/select-inputs/journal-bank-accounts-dropdown";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

type Props = {
  name: string;
  label?: string;
  options: JournalBankAccountDropdownProps["items"];
  autoFocus?: boolean;
  placeholder?: JournalBankAccountDropdownProps["placeholder"];
  description?: string;
  fullWidth?: JournalBankAccountDropdownProps["fullWidth"];
  className?: JournalBankAccountDropdownProps["className"];
  containerClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
  direction?: "horizontal" | "vertical";
  sideOffset?: number;
  allowReset?: boolean;
  allowUnknownAccount?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  variant?: "outline" | "ghost";
  container?: React.RefObject<HTMLElement>;
  modal?: boolean;
  onCreate?: JournalBankAccountDropdownProps["onCreate"];
};

export const JournalBankAccountDropdown = ({
  label,
  name,
  options,
  allowReset = false,
  fullWidth = true,
  placeholder,
  description,
  containerClassName,
  direction = "vertical",
  className,
  sideOffset,
  readOnly,
  disabled,
  variant = "outline",
  allowUnknownAccount,
  container,
  modal,
  onCreate,
}: Props) => {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const currentOption = useMemo(
          () =>
            options.find((option) => option.journalAccountId === field.value),
          [options, field.value],
        );

        const value = currentOption
          ? {
              label: currentOption.name ?? "",
              value: currentOption.journalAccountId,
              subLabel: currentOption.number ?? "",
              alternativeLabels: [],
            }
          : undefined;

        const entityId = form.getValues("entityId");

        return (
          <FormItem>
            <div
              className={cn(
                direction === "horizontal" &&
                  "flex flex-row items-center space-x-4",
                containerClassName,
              )}
            >
              {label && <FormLabel>{label}</FormLabel>}
              <FormControl>
                <div className="w-full flex flex-row gap-1">
                  <JournalBankAccountsDropdown
                    currentOption={value}
                    items={options}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                    className={className}
                    fullWidth={fullWidth}
                    placeholder={placeholder}
                    sideOffset={sideOffset}
                    variant={variant}
                    readOnly={readOnly}
                    disabled={disabled}
                    allowUnknownAccount={allowUnknownAccount}
                    container={container}
                    modal={modal}
                    entityId={entityId}
                    onCreate={onCreate}
                  />
                  {allowReset && value && (
                    <Button
                      type="reset"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        field.onChange(null);
                      }}
                      size="icon"
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
              </FormControl>
              {description && <FormDescription>{description}</FormDescription>}
              <FormMessage />
            </div>
          </FormItem>
        );
      }}
    />
  );
};
